@if ((data.stackedCard.thingie$ | async); as thingie) {
  <ng-template appDialogTitleRowExtraContent>
    <div class="w-full ml-4 grow-0 flex items-center gap-4">
      <app-thingie-state-chip
        [thingies]="(data.stackedCard.thingieIds | pureFnCall:getThingiesByIds | async) ?? []"
        matTooltip="Combined replicate state" />

      <!-- Replicate count & replicate selector -->
      <button
        mat-icon-button
        #t="matMenuTrigger"
        [ngClass]="{'menuOpen': t.menuOpen}"
        [matMenuTriggerFor]="replicatesMenu"
        class="replicateBox !w-auto"
        [matTooltip]="data.stackedCard.thingieIds.length + ' replicates in experiment. Select to view replicate details.'"
        >
        <span class="text-xl">{{data.stackedCard.thingieIds.length}}</span>
        <ion-icon src="/assets/svg/replicates-cards.svg" class="ml-1"></ion-icon>
        <ion-icon name="chevron-down" class="ml-1/2"></ion-icon>
      </button>
      <!-- Menu opened for replicate selection by the replicate count/selector-button -->
      <mat-menu #replicatesMenu>
        @if(_sortedResolvedThingiesFromCardThingieIdList$ | async; as replicateThingies) {
          @for(replicate of replicateThingies; track replicate._id) {
            <button
              class="!max-w-96"
              mat-menu-item
              (click)="routeToReplicate(urls.route.thingieDetail(replicate._id!))">
              <app-string-ellipsis
                [input]="replicate.name">
              </app-string-ellipsis>
            </button>
          }
        }
      </mat-menu>

      <!-- Push subsequent content to the right -->
      <div class="grow"></div>

      @if((data.stackedCard.anyArchived$ | async) === true && archivedStatusInfo) {
        <app-thingie-state-chip [state]="archivedStatusInfo"></app-thingie-state-chip>
      }

      <!-- popup mode (one big graph vs smaller cards per replicate) -->
      <mat-button-toggle-group
        [value]="data.popupMode"
        (change)="changePopupMode(data, $event)"
        class="mat-button-toggle-group-icon mat-accent spaceRight"
        >
        <mat-button-toggle
          value="stacked"
          matTooltip="Display replicates in one graph"
          >
          <ion-icon src="/assets/svg/single-graph-view.svg"></ion-icon>
        </mat-button-toggle>
        <mat-button-toggle
          value="single"
          matTooltip="Display replicates individually"
          >
          <ion-icon src="/assets/svg/grid-view.svg"></ion-icon>
        </mat-button-toggle>
      </mat-button-toggle-group>

      @if (data.popupMode === 'stacked') {
        @if (data.stackedCard.isPinned) {
          <button mat-icon-button
            (click)="removePinnedCard(data.stackedCard.thingieIds[0], data.stackedCard)"
            matTooltip="Un-pin card"
            >
            <ion-icon class="pinIcon" src="/assets/svg/pin.svg"></ion-icon>
          </button>
        }
        @if (!data.stackedCard.isPinned) {
          <button mat-icon-button
            (click)="addPinnedCard(data.stackedCard.thingieIds[0], data.stackedCard)"
            matTooltip="Pin card"
            >
            <ion-icon src="/assets/svg/pin-outline.svg"></ion-icon>
          </button>
        }
      }

      <!-- Note
      - removed, because of pop-up blocking logic in browser
      and/or in pop-up blockers, making this feature unreliable for now
      - see https://aquila-biolabs.atlassian.net/browse/COSOFT-2477
      - Will be re-added in the future, when we have a better solution
      -->
      <!-- <button
      mat-icon-button
      (click)="openThingieTabs(data.stackedCard.thingieIds)"
      matTooltip="Open replicates in new tabs. (Might be blocked depending on your browser settings)"
      >
      <ion-icon name="open-outline"></ion-icon>
    </button> -->

      @if(!isEditor()) {
        <div [matTooltip]="'You need to have editor permissions in this project to open experiment conversations.'">
          <button
            mat-icon-button
            disabled
            class="copilot-history-trigger"
            aria-label="Open experiment conversations"
            >
            <ion-icon name="chatbubble-ellipses-outline" />
          </button>
        </div>
      } @else {
    <button
      mat-icon-button
        class="copilot-history-trigger"
        [matMenuTriggerFor]="copilotHistoryMenu"
        [matTooltip]="(data.stackedCard.groupArchived$ | async) ? 'View shared experiment conversations. Starting a new one is unavailable while the experiment is archived.' : 'Open experiment conversations'"
        aria-label="Open experiment conversations"
        >
        @if (recentCopilotConversations$ | async; as recentConversations) {
          <div
            class="copilot-history-badge"
            [matBadgeHidden]="recentConversations.length === 0"
            [matBadge]="getConversationBadgeLabel(recentConversations.length)"
            [matBadgeOverlap]="true"
            matBadgeColor="accent"
      >
      <ion-icon name="chatbubble-ellipses-outline" />
          </div>
        }
    </button>
      }

      <mat-menu #copilotHistoryMenu="matMenu">
        <ng-template matMenuContent>
          <div class="copilot-history-menu">
            <button
              mat-menu-item
              [disabled]="data.stackedCard.groupArchived$ | async"
              (click)="_openCopilot()"
              >
              <ion-icon name="add-outline" />
              Start New Experiment Chat
            </button>

            <mat-divider class="![--mat-divider-width:3px]" />

            @if (recentCopilotConversations$ | async; as recentConversations) {
              @if (recentConversations.length > 0) {
                <div class="copilot-history-menu__section-label">
                  Recent Conversations
                </div>

                @for (conversation of recentConversations; track conversation.id) {
                  <button
                    mat-menu-item
                    class="copilot-history-item"
                    (click)="openCopilotConversation(conversation.id)"
                    [matTooltip]="conversation.contextLabel || conversation.title"
                    >
                    <div class="copilot-history-item__content">
                      <span class="copilot-history-item__title">{{ conversation.title }}</span>
                      <span class="copilot-history-item__meta">
                        Updated {{ formatConversationUpdatedAt(conversation.updatedAt) }}
                      </span>
                    </div>
                  </button>
                }
              } @else {
                <div class="copilot-history-menu__empty">
                  No recent experiment conversations yet.
                </div>
              }
            }

            @if (data.stackedCard.groupArchived$ | async) {
              <div class="copilot-history-menu__empty">
                New experiment chats are unavailable while this experiment is archived.
              </div>
            }
          </div>
        </ng-template>
      </mat-menu>

    <!-- Actions menu -->
    <button
      mat-icon-button
      [matMenuTriggerFor]="cardStackModalActions"
      data-cy="stack_card_dialog__actions_menu_trigger"
    >
      <ion-icon name="menu-outline" />
    </button>
    <mat-menu #cardStackModalActions>
      <!-- Edit experiment -->
        @if ((data.stackedCard.anyArchived$ | async) === false) {
        <button
          mat-menu-item
          [disabled]="!isEditor()"
          (click)="$event.preventDefault(); _editExperiment(thingie)"
          [matTooltip]="!isEditor() ? 'You need to have editor permissions in this project to edit this experiment.' : 'Edit experiment'"
          data-cy="stack_card_dialog__actions_menu__edit_experiment"
        >
          <ion-icon class="menu-icon" name="pencil-outline" />
          Edit Experiment
        </button>
      }
      
      <mat-divider class="![--mat-divider-width:3px]" />

      <!-- Duplicate experiment -->
      <button
        mat-menu-item
        [disabled]="!isEditor()"
        (click)="$event.preventDefault(); _duplicateExperiment(thingie)"
        [matTooltip]="!isEditor() ? 'You need to have editor permissions in this project to duplicate this experiment.' : 'Duplicate experiment'"
      >
        <ion-icon src="/assets/custom-icons/copy-e-outline.svg" />
        Duplicate Experiment
      </button>

      <mat-divider class="![--mat-divider-width:3px]" />

      <!-- Edit graph config -->
      @if (data.popupMode === 'stacked') {
        <button
          mat-menu-item
          (click)="_openGraphConfiguration()"
          matTooltip="Configure graph"
          data-cy="stack_card_dialog__actions_menu__edit_graph_config"
        >
          <ion-icon src="/assets/svg/graph-edit.svg" />
          Edit Graph
        </button>
      }

      <!-- Add annotation-->
      @if(!isEditor()) {
        <button
          mat-menu-item
          [disabled]="true"
          [matTooltip]="'You need to have editor rights in this project to add annotation.'"
          >
          <ion-icon src="/assets/svg/add-annotation-outline.svg" />
          Add Annotation
        </button>
      } @else {
        <button
          mat-menu-item
          [disabled]="data.stackedCard.groupArchived$ | async"
          (click)="_addAnnotation(data.stackedCard.thingieIds)"
          [matTooltip]="(data.stackedCard.groupArchived$ | async) ? 'Adding annotations is not available for archived experiments. Please un-archive your experiment first if you want to add annotation.' : 'Add annotation'"
          >
          <ion-icon src="/assets/svg/add-annotation-outline.svg" />
          Add Annotation
        </button>
      }

      <!-- Open Copilot -->
      @if(!isEditor()){
        <button
          mat-menu-item
          disabled
          [matTooltip]="'You need to have editor permissions in this project to open a shared Copilot conversation.'"
          >
          <ion-icon name="chatbubble-ellipses-outline" />
          Open Copilot
        </button>
      } @else {
        <button
          mat-menu-item
          [disabled]="data.stackedCard.groupArchived$ | async"
          (click)="_openCopilot()"
          [matTooltip]="(data.stackedCard.groupArchived$ | async) ? 'Opening Copilot is not available for archived experiments. Please un-archive your experiment first if you want to continue a shared conversation.' : 'Open shared Copilot chat for this experiment'"
          >
          <ion-icon name="chatbubble-ellipses-outline" />
          Open Copilot
        </button>
      }

      <mat-divider class="![--mat-divider-width:3px]" />

      <!-- Archive/Unarchive -->
      <ng-container
        [ngTemplateOutlet]="archiving"
        [ngTemplateOutletContext]="{ 
          isRunning: data.stackedCard.groupRunning$ | async,
          isArchived: data.stackedCard.groupArchived$ | async,
          canBeArchived: data.stackedCard.canBeArchived$ | async
        }" />
      <ng-template
        #archiving
        let-isArchived="isArchived"
        let-isRunning="isRunning"
        let-canBeArchived="canBeArchived"
        >
        <!-- Unarchive -->
        @if (isArchived) {
          @if(isEditor()) {
            <button
              mat-menu-item
              [disabled]="isRunning"
              (click)="updateArchiveProperty(data.stackedCard, 'unarchive')"
              >
              <ion-icon class="menu-icon" src="/assets/svg/unarchive.svg" />
              Unarchive Experiment
            </button>
          } @else {
            <button
              mat-menu-item
              disabled
              [matTooltip]="'You need to have editor permissions in this project to unarchive this experiment.'"
              >
              <ion-icon class="menu-icon" src="/assets/svg/unarchive.svg" />
              Unarchive Experiment
            </button>
          }
        }
        <!-- Archive (if unarchive-button is not shown)-->
        @if(!isArchived) {
          @if(isEditor()) {
            <button
              mat-menu-item
              [disabled]="isRunning || !canBeArchived"
              [matTooltip]="isRunning ? 'Replicates must not have running tasks to archive' : 'Archive all replicates'"
              (click)="updateArchiveProperty(data.stackedCard, 'archive')"
              >
              <ion-icon class="menu-icon" name="archive-outline" />
              Archive Experiment
            </button>
          } @else {
            <button
              mat-menu-item
              disabled
              [matTooltip]="'You need to have editor permissions in this project to archive this experiment.'"
              >
              <ion-icon class="menu-icon" name="archive-outline" />
              Archive Experiment
            </button>
          }
        }
      </ng-template>

      <mat-divider class="![--mat-divider-width:3px]" />


      <!-- Export data -->
      <button
        mat-menu-item
        (click)="excelExport(data.stackedCard.thingieIds)"
        matTooltip="Export data"
        >
        <ion-icon name="download-outline" />
        Export
      </button>
    </mat-menu>
  </div>
  </ng-template>
}

<div class="h-full flex mat-typography">
  <div
    class="mr-2 flex-1 flex justify-center"
    [class.mt-16]="data.popupMode === 'single'"
    [class.pb-32]="data.popupMode === 'single'"
    >
    @if (data.popupMode === 'stacked') {
      <div class="mat-mdc-card-content stacked-card-popup">
        <!-- replace chart-view-tab when graphTabRevision is changed -->
        @for (revision of [graphTabRevision()]; track revision ) {
          <app-chart-view-tab
            (chartIsLoaded)="_graphsChartIsLoaded()"
            class="h-full"
            [tab]="data.stackedCard.popupTab"
            legendPosition="bottom"
            >
          </app-chart-view-tab>
        }
      </div>
    }
    @if (data.popupMode === 'single') {
      <app-thingie-card-list
        [selectedCards]="dialogParams.singleCards"
        [cards]="dialogParams.singleCards"
        idPrefix="stack-card-dialog"
        (selectedStackCard)="_closeDialog()">
      </app-thingie-card-list>
    }
  </div>
</div>


<ng-template #stackedModeTaskStatusBarButton>
  @if (data.popupMode === 'stacked') {
    <div class="w-full flex gap-2">
      @if ((data.stackedCard.thingieIds | pureFnCall:getThingiesByIds | async) || []; as thingies) {
        <app-task-status-bar class="flex-1" [taskKey]="{thingies: thingies}" />
      }
    </div>
  }
</ng-template>
